import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { ShippingCaseResponse } from "@pages/shippingView/types";
import {
  useOutletContext,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { Tabs, Tab, Paper } from "@mui/material";
import { RootState } from "@store/index";
import { getVisibleUnloadingSubPanels } from "@pages/shippingView/helpers/tabs-mapping";

export default function Unloading() {
  const navigate = useNavigate();
  const location = useLocation();
  const { oid, sid } = useParams();
  const { shipping } = useOutletContext<{ shipping: ShippingCaseResponse }>();
  const role = useSelector((state: RootState) => state.auth.user?.role) || "";
  const { type_of_shipment } = shipping.offer;
  const detailsLabel =
    type_of_shipment === "BULK" ? "Bulk" : "Unloading Details";

  const visiblePanels = useMemo(
    () => getVisibleUnloadingSubPanels(role),
    [role],
  );

  const tabs = useMemo(
    () =>
      visiblePanels.map((panel) => ({
        path: panel.path,
        label:
          panel.path === "details"
            ? detailsLabel
            : ({
                warehousing: "Warehousing",
                "deposit-warehouse": "Deposit / Warehouse Shifting",
                "depositary-change": "Depositary Change",
                "picking-service": "Picking Service",
                fcr: "FCR",
              }[panel.path] ?? panel.path),
      })),
    [visiblePanels, detailsLabel],
  );

  const activeTab = tabs.findIndex((t) =>
    location.pathname.includes(`/unloading/${t.path}`),
  );

  useEffect(() => {
    if (!tabs.length || !oid || !sid) return;

    const hasVisiblePath = tabs.some((tab) =>
      location.pathname.includes(`/unloading/${tab.path}`),
    );

    if (!hasVisiblePath) {
      navigate(
        `/offers/${oid}/shipping-case/${sid}/unloading/${tabs[0].path}`,
        { replace: true },
      );
    }
  }, [tabs, location.pathname, navigate, oid, sid]);

  if (!tabs.length) {
    return null;
  }

  return (
    <>
      <Tabs
        className="cargo"
        value={activeTab === -1 ? 0 : activeTab}
        onChange={(_, val) => navigate(tabs[val].path)}
      >
        {tabs.map((t) => (
          <Tab key={t.path} label={t.label} />
        ))}
      </Tabs>

      <Paper
        elevation={0}
        sx={{
          p: "16px",
          border: "1px solid rgba(0, 0, 0, 0.12)",
          borderRadius: "0 4px 4px 4px",
        }}
      >
        <Outlet context={{ shipping }} />
      </Paper>
    </>
  );
}
